import { Box, styled } from "@mui/material";
import { RenderStateCamera } from "@novorender/api";
import { vec2 } from "gl-matrix";
import { useCallback, useEffect, useRef, useState } from "react";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { telemetry } from "telemetry";
import { AsyncStatus } from "types/misc";
import { log } from "utils/log";

import { LargeMapWeb } from "./largeMapWeb";
import {
    selectCurrentDrawing,
    selectMiniMapDrawings,
    selectMiniMapLevels,
    setCurrentDrawing,
    setDrawingData,
} from "./minimapSlice";
import {
    calculateImageFitBox,
    drawPositionIndicator,
    fetchDrawingData,
    findDrawingIdByElevation,
    modelToDrawingSpace,
} from "./miniMapUtils";
import { DrawingSpacePosition } from "./types";
import { useMiniMapData } from "./useMiniMapData";

const l = log.miniMap.miniMapWeb;

const MiniMapContainer = styled(Box)({
    display: "flex",
    flexDirection: "column",
    gap: 10,
    alignItems: "flex-end",
    overflow: "visible",
    position: "absolute",
    bottom: 16,
    right: 16,
    pointerEvents: "auto",
});

const CanvasWrapper = styled(Box)({
    position: "relative",
    "&:hover .hover-layer": {
        borderColor: "rgba(255, 255, 255, 0.5)",
        boxShadow: "0px 0px 28px 0px rgba(0, 0, 0, 0.45)",
        opacity: 1,
        transform: "scale(1.25)",
    },
    "&:hover canvas": {
        transform: "scale(1.25)",
        boxShadow: "none",
    },
});

const HoverLayer = styled(Box)({
    position: "absolute",
    border: "4px solid transparent",
    borderRadius: 12,
    top: -3,
    right: -3,
    bottom: -3,
    left: -3,
    pointerEvents: "none",
    transition: "all 300ms ease-out",
    transformOrigin: "right bottom",
    boxShadow: "0px 0px 0px 0px rgba(0, 0, 0, 0)",
    opacity: 1,
});

const StyledCanvas = styled("canvas")({
    display: "block",
    transformOrigin: "right bottom",
    transition: "all 300ms ease-out",
    borderRadius: 8,
    boxShadow: "0px 0px 20px 0px rgba(0, 0, 0, 0.2)",
    cursor: "pointer",
    backgroundColor: "white",
});

interface MiniMapProps {
    className?: string;
}

function MiniMap({ onClick }: { onClick?: () => void }) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const currentDrawing = useAppSelector(selectCurrentDrawing);
    const levels = useAppSelector(selectMiniMapLevels);
    const drawings = useAppSelector(selectMiniMapDrawings);
    const dispatch = useAppDispatch();
    const {
        state: { view, internalEventEmitter },
    } = useExplorerGlobals();

    const [drawingPosition, setDrawingPosition] = useState<DrawingSpacePosition>({
        position: vec2.fromValues(0, 0),
        rotation: 0,
    });

    const canvasWidth = 240;
    const canvasHeight = 172;

    const drawMap = useCallback(
        (
            ctx: CanvasRenderingContext2D,
            img: ImageBitmap,
            canvasWidth: number,
            canvasHeight: number,
            drawingPosition: DrawingSpacePosition,
        ) => {
            ctx.clearRect(0, 0, canvasWidth, canvasHeight);
            const { width, height, x, y } = calculateImageFitBox({
                imageDimensions: [img.width, img.height],
                canvasDimensions: [canvasWidth, canvasHeight],
            });
            const positionX = drawingPosition.position[0] * width + x;
            const positionY = drawingPosition.position[1] * height + y;
            const rotation = drawingPosition.rotation;
            ctx.drawImage(img, x, y, width, height);
            drawPositionIndicator(ctx, positionX, positionY, rotation);
        },
        [],
    );

    useEffect(() => {
        if (!view || !internalEventEmitter || levels.length === 0) {
            return;
        }

        const handleCameraUpdated = (camera: RenderStateCamera) => {
            const cameraZ = camera.position[2];

            const targetDrawingId = findDrawingIdByElevation(cameraZ, levels, currentDrawing?.id);
            if (targetDrawingId && targetDrawingId !== currentDrawing?.id) {
                l.debug(`Updating drawing due to elevation change. From ${currentDrawing?.id} to ${targetDrawingId}`);
                dispatch(setCurrentDrawing(targetDrawingId));

                // Fetch the drawing data for the new drawing if we don't have it already
                const targetDrawing = drawings[targetDrawingId];
                if (!targetDrawing) {
                    l.error("Target drawing not found in state for id:", targetDrawingId);
                } else {
                    const { status } = targetDrawing.drawingData.asyncState;
                    if (status === AsyncStatus.Initial) {
                        l.debug("Fetching drawing data for new drawing:", targetDrawing);
                        dispatch(
                            setDrawingData({
                                drawingId: targetDrawing.id,
                                asyncState: { status: AsyncStatus.Loading },
                            }),
                        );
                        fetchDrawingData(targetDrawing.drawingData.url)
                            .then((imgBitmap) => {
                                l.debug("Successfully fetched drawing data for mini map:", targetDrawing.id, imgBitmap);
                                dispatch(
                                    setDrawingData({
                                        drawingId: targetDrawing.id,
                                        asyncState: { status: AsyncStatus.Success, data: imgBitmap },
                                    }),
                                );
                            })
                            .catch((error) => {
                                const e = error as Error;
                                l.error("Failed to fetch drawing data for mini map:", e);
                                telemetry.trackCaughtException({
                                    source: "miniMap.miniMapWeb.fetchDrawingData",
                                    category: "render",
                                    error,
                                    severity: "error",
                                    extraAttributes: { drawingId: targetDrawing.id },
                                });
                                dispatch(
                                    setDrawingData({
                                        drawingId: targetDrawing.id,
                                        asyncState: { status: AsyncStatus.Error, msg: e.message },
                                    }),
                                );
                            });
                    } else {
                        l.debug("Drawing data for target drawing already in state, no need to fetch:", targetDrawing);
                    }
                }
            }

            if (currentDrawing?.modelAB && currentDrawing?.drawingAB) {
                const result = modelToDrawingSpace(currentDrawing.modelAB, currentDrawing.drawingAB, camera);
                setDrawingPosition(result);
            }
        };

        handleCameraUpdated(view.renderState.camera);

        const unsubscribe = internalEventEmitter.on("cameraUpdated", handleCameraUpdated);

        return () => {
            unsubscribe();
        };
    }, [view, internalEventEmitter, levels, currentDrawing, dispatch, drawings]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) {
            return;
        }

        const ctx = canvas.getContext("2d");
        if (!ctx) {
            return;
        }

        const dpr = window.devicePixelRatio || 1;

        canvas.width = canvasWidth * dpr;
        canvas.height = canvasHeight * dpr;
        canvas.style.width = `${canvasWidth}px`;
        canvas.style.height = `${canvasHeight}px`;
        ctx.scale(dpr, dpr);

        if (!currentDrawing) {
            l.debug("No current drawing selected for mini map, drawing placeholder");
            drawBackground(ctx, canvasWidth, canvasHeight);
            return;
        }

        const { status } = currentDrawing.drawingData.asyncState;
        l.debug(
            "Drawing mini map, currentDrawing:",
            currentDrawing.id,
            "state:",
            currentDrawing.drawingData.asyncState,
        );
        switch (status) {
            case AsyncStatus.Initial:
            case AsyncStatus.Loading:
                drawBackground(ctx, canvasWidth, canvasHeight);
                break;
            case AsyncStatus.Success:
                drawMap(ctx, currentDrawing.drawingData.asyncState.data, canvasWidth, canvasHeight, drawingPosition);
                break;
            case AsyncStatus.Error:
            default:
                drawBackground(ctx, canvasWidth, canvasHeight);
        }
    }, [currentDrawing, drawingPosition, canvasWidth, canvasHeight, drawMap]);

    return <StyledCanvas ref={canvasRef} onClick={onClick} />;
}

function drawBackground(ctx: CanvasRenderingContext2D, width: number, height: number) {
    const gradient = ctx.createLinearGradient(0, 0, width, height);
    gradient.addColorStop(0, "#a0a0a0");
    gradient.addColorStop(1, "#d0d0d0");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);
}

export function MiniMapWeb({ className }: MiniMapProps) {
    const [isSheetOpen, setIsSheetOpen] = useState(false);

    const handleMiniMapClick = () => {
        setIsSheetOpen(true);
    };

    const handleSheetClose = () => {
        setIsSheetOpen(false);
    };

    useMiniMapData();

    const currentDrawing = useAppSelector(selectCurrentDrawing);

    if (currentDrawing === null) {
        return;
    }

    return (
        <>
            <MiniMapContainer className={className}>
                <CanvasWrapper>
                    <HoverLayer className="hover-layer" />
                    <MiniMap onClick={handleMiniMapClick} />
                </CanvasWrapper>
            </MiniMapContainer>

            <LargeMapWeb open={isSheetOpen} onClose={handleSheetClose} />
        </>
    );
}
